import { unified } from "unified";
import rehypeStringify from "rehype-stringify";
import { mystParser } from "myst-parser";
import { State, formatHtml, mystToHast, transform } from "myst-to-html";

const renderPipeline = unified()
  .use(mystParser)
  .use(transform, new State())
  .use(mystToHast)
  .use(formatHtml)
  .use(rehypeStringify);

function renderDropdownAdmonitions(html: string): string {
  if (typeof document === "undefined") {
    return html.replace(
      /<aside class="dropdown admonition ([^"]*)"(?: open)?>([\s\S]*?)<\/aside>/g,
      (_match, kind: string, inner: string) => {
        const titleMatch = inner.match(/^\s*<p class="admonition-title">([\s\S]*?)<\/p>([\s\S]*)$/);
        if (!titleMatch) {
          return `<details class="dropdown admonition ${kind}">${inner}</details>`;
        }
        const [, title, body] = titleMatch;
        return `<details class="dropdown admonition ${kind}"><summary class="admonition-title">${title}</summary>${body}</details>`;
      },
    );
  }

  const template = document.createElement("template");
  template.innerHTML = html;

  for (const aside of Array.from(template.content.querySelectorAll("aside.dropdown.admonition"))) {
    const title = Array.from(aside.children).find((child) => child.classList.contains("admonition-title"));
    if (!title) continue;

    const details = document.createElement("details");
    details.className = Array.from(aside.classList).filter((className) => className !== "open").join(" ");
    details.open = aside.classList.contains("open") || aside.hasAttribute("open");

    const summary = document.createElement("summary");
    summary.className = title.className;
    summary.innerHTML = title.innerHTML;
    details.appendChild(summary);

    for (const node of Array.from(aside.childNodes)) {
      if (node === title) continue;
      details.appendChild(node);
    }

    aside.replaceWith(details);
  }

  return template.innerHTML;
}

function wrapExecutableCodeBlocks(html: string): string {
  if (typeof document === "undefined") {
    return html.replace(
      /<pre([^>]*)>\s*<code class="([^"]*(?:language-python|language-py|language-python3|language-ipython)[^"]*)">([\s\S]*?)<\/code>\s*<\/pre>/g,
      '<div class="thebe-cell cell-loading" data-thebe-cell><div class="cell-spinner" data-cell-spinner><div class="cell-spinner-ring"></div><span class="cell-spinner-label">Code cell</span></div><pre$1 data-executable class="cell-pending"><code class="$2">$3</code></pre><div class="thebe-output" data-output></div></div>',
    );
  }

  const template = document.createElement("template");
  template.innerHTML = html;

  for (const code of Array.from(template.content.querySelectorAll("pre > code"))) {
    const isExecutable = Array.from(code.classList).some((className) =>
      /^language-(python|py|python3|ipython)$/.test(className),
    );
    if (!isExecutable) continue;

    const pre = code.parentElement;
    if (!pre) continue;

    pre.setAttribute("data-executable", "");
    pre.classList.add("cell-pending");

    const cell = document.createElement("div");
    cell.className = "thebe-cell cell-loading";
    cell.dataset.thebeCell = "";

    const spinner = document.createElement("div");
    spinner.className = "cell-spinner";
    spinner.dataset.cellSpinner = "";
    const ring = document.createElement("div");
    ring.className = "cell-spinner-ring";
    const label = document.createElement("span");
    label.className = "cell-spinner-label";
    label.textContent = "Code cell";
    spinner.appendChild(ring);
    spinner.appendChild(label);

    const output = document.createElement("div");
    output.className = "thebe-output";
    output.dataset.output = "";

    pre.insertAdjacentElement("beforebegin", cell);
    cell.appendChild(spinner);
    cell.appendChild(pre);
    cell.appendChild(output);
  }

  return template.innerHTML;
}

export function render(_title: string, mystText: string): string {
  return wrapExecutableCodeBlocks(renderDropdownAdmonitions(String(renderPipeline.processSync(mystText).value)));
}
